import { Anchor, Breadcrumbs, Grid, Text } from '@mantine/core';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { Dashboard } from 'survey-analytics';
import { Model } from 'survey-core';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import MetricsSelector from '../../components/MetricsSelector';
import { Page } from '../../constants/pageTitles';
import { daysAgoISODate, todayISODate } from '../../helpers/dateUtils';
import useFetch from '../../helpers/useFetch';

const statsDebounced = AwesomeDebouncePromise((fetch, accountId, domainId, surveyId, startDate, endDate) => {
  return fetch(
    `/api/admin/accounts/${accountId}/domains/${domainId}/surveys/${surveyId}/submissions?startDate=${startDate}&endDate=${endDate}`
  );
}, 300);

const page = `${Page['survey']}`;

const SurveysStatisticsPage = () => {
  const fetch = useFetch();

  const [survey, setSurvey] = useState();
  const [error, setError] = useState();
  const { accountId, domainId, surveyId } = useParams();

  const initialPeriodDays = 7;

  // Store ISO date strings (YYYY-MM-DD)
  const [startDate, setStartDate] = useState(daysAgoISODate(initialPeriodDays - 1));
  const [endDate, setEndDate] = useState(todayISODate());
  const [responses, setResponses] = useState();

  useEffect(() => {
    statsDebounced(fetch, accountId, domainId, surveyId, startDate, endDate)
      .then((responses) => {
        setResponses(responses);
      })
      .catch(setError);
  }, [accountId, domainId, endDate, fetch, startDate, surveyId]);

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/surveys/${surveyId}`)
      .then(({ survey }) => setSurvey(survey))
      .catch(setError);
  }, [accountId, domainId, surveyId, fetch]);

  useEffect(() => {
    if (!responses || !survey) {
      return undefined;
    }
    const dashboard = new Dashboard({
      questions: new Model(survey.config).getAllQuestions(),
      data: responses,
      showToolbar: false,
    });
    dashboard.render('surveyVizPanel');
    // destroy() empties the container it rendered into.
    return () => dashboard.destroy();
  }, [responses, survey]);

  return (
    <>
      <Metadata title={`${page} | ${survey?.name || 'Unnamed survey'}`} />
      <Breadcrumbs mb="md">
        <Anchor href={`#/accounts/${accountId}/domains/${domainId}/analytics`}>Home</Anchor>
        <Anchor href={`#/accounts/${accountId}/domains/${domainId}/surveys`}>{page}</Anchor>
        <Text>{survey?.name || 'Unknown Survey'}</Text>
      </Breadcrumbs>
      <Grid>
        <Grid.Col>
          <MetricsSelector
            startDate={startDate}
            endDate={endDate}
            setStartDate={setStartDate}
            setEndDate={setEndDate}
            rowSize="12"
            scope="surveys"
            id={surveyId}
          />
          {error ? <ErrorMessage message={error.message} /> : <div id="surveyVizPanel" />}
        </Grid.Col>
      </Grid>
    </>
  );
};

export default SurveysStatisticsPage;
